import type { jsPDF } from 'jspdf';
import type {
  autoTable as AutoTableFn,
  CellDef,
  CellHookData,
  HookData,
  RowInput,
  Styles,
} from 'jspdf-autotable';

import type {
  TableExportFormatter,
  TablePdfCallout,
  TablePdfDocument,
  TablePdfGroupRow,
  TablePdfNote,
  TablePdfRow,
  TablePdfSummaryBlock,
  TablePdfTable,
  TablePdfTotal,
} from './table-export.model';
import { tableExportText } from './table-export-text.util';

type Rgb = [number, number, number];
type AutoTable = typeof AutoTableFn;

/*
 * Frame and palette follow the attendance export
 * (`libs/domains/school/feature-attendance/src/lib/attendance-export.util.ts`),
 * so every PDF the system prints looks like one family. jsPDF paints RGB, not
 * CSS tokens.
 */
const PAGE_MARGIN_MM = 14;
const FOOTER_SPACE_MM = 20;
/** Space a table's heading takes above it. */
const TABLE_HEADING_MM = 7;
/**
 * A heading plus the header row and a line of its table. With less than this
 * left on the page, the table starts on the next one, so a heading never ends
 * a page alone.
 */
const TABLE_HEADING_KEEP_MM = 24;
/** About one closing row's height: each extra one is kept with the heading too. */
const TOTAL_ROW_MM = 7.5;
/** Space between a table (or its notes) and the next table. */
const TABLE_GAP_MM = 8;
const LOGO_MM = 5;
const LOGO_MAX_WIDTH_MM = 15;
/** From the gym's name to the first line of `tenantDetails` under it. */
const TENANT_DETAILS_LINE_MM = 3.5;
/** Space between a callout's border and its content. */
const CALLOUT_PADDING_MM = 4;
/** Space between the callout and what follows it. */
const CALLOUT_GAP_MM = 5;
const PILL_HEIGHT_MM = 5;
const PILL_PADDING_MM = 2;
/** Space between two pills, and between a pill and a joiner word. */
const PILL_GAP_MM = 1.6;
/** A line of pills and the space under it. */
const PILL_LINE_MM = PILL_HEIGHT_MM + 1.6;
const FOREGROUND: Rgb = [23, 23, 23];
const SECONDARY_TEXT: Rgb = [74, 78, 85];
const MUTED: Rgb = [107, 112, 121];
const BORDER: Rgb = [225, 225, 225];
const HEADER_FILL: Rgb = [243, 244, 244];
const GROUP_FILL: Rgb = [249, 250, 250];
const STAMP_FILL: Rgb = [221, 233, 248];
const STAMP_TEXT: Rgb = [9, 97, 204];
const ACCENT: Rgb = STAMP_TEXT;
/**
 * The Windows-1252 characters above U+00FF, which the standard fonts draw:
 * € ‚ ƒ „ … † ‡ ˆ ‰ Š ‹ Œ Ž ‘ ’ “ ” • – — ˜ ™ š › œ ž Ÿ.
 */
const WIN_ANSI_EXTRAS = new Set(
  '\u20ac\u201a\u0192\u201e\u2026\u2020\u2021\u02c6\u2030\u0160\u2039\u0152\u017d' +
    '\u2018\u2019\u201c\u201d\u2022\u2013\u2014\u02dc\u2122\u0161\u203a\u0153\u017e\u0178'
);
/** Spaces beyond WinAnsi, e.g. the narrow one `Intl` puts before "AM". */
const WIDE_SPACES = /[\u2000-\u200a\u202f\u205f\u3000]/g;

/**
 * Text the standard fonts can draw. A single character beyond WinAnsi (an
 * emoji in a gym's or a client's name) makes jsPDF write the whole string in
 * 16 bits, which prints a name led by a globe emoji as "Ø<ß F r a n c i s c o".
 * Those characters are dropped, and the spaces left around them tidied. Text
 * without them is kept as it came.
 */
function drawableText(text: string): string {
  // An accent typed apart from its letter (e + U+0301) is joined to it first,
  // so "José" keeps its "é".
  const spaced = text.normalize('NFC').replace(WIDE_SPACES, ' ');
  const kept = Array.from(spaced)
    .filter(
      (char) => (char.codePointAt(0) ?? 0) <= 0xff || WIN_ANSI_EXTRAS.has(char)
    )
    .join('');
  return kept.length === spaced.length
    ? spaced
    : kept.replace(/ {2,}/g, ' ').trim();
}

function isGroupRow(row: TablePdfRow): row is TablePdfGroupRow {
  return !Array.isArray(row);
}

function lastTableY(doc: jsPDF, fallback: number): number {
  return (
    (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable
      ?.finalY ?? fallback
  );
}

function drawStamp(doc: jsPDF, stamp: string, offset: number): number {
  const width = doc.internal.pageSize.getWidth();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  const pillWidth = doc.getTextWidth(stamp) + 6;
  const x = width - PAGE_MARGIN_MM - pillWidth;
  doc.setFillColor(...STAMP_FILL);
  doc.roundedRect(x, 18 + offset, pillWidth, 6, 3, 3, 'F');
  doc.setTextColor(...STAMP_TEXT);
  doc.text(stamp, x + pillWidth / 2, 21 + offset, {
    align: 'center',
    baseline: 'middle',
  });
  return pillWidth + 4;
}

function drawHeader(doc: jsPDF, report: TablePdfDocument): number {
  const width = doc.internal.pageSize.getWidth();
  const contentWidth = width - PAGE_MARGIN_MM * 2;
  let nameX = PAGE_MARGIN_MM;
  if (report.logo) {
    const logoWidth = Math.min(LOGO_MM * report.logo.ratio, LOGO_MAX_WIDTH_MM);
    doc.addImage(report.logo.dataUrl, 'PNG', PAGE_MARGIN_MM, 12.6, logoWidth, LOGO_MM);
    nameX += logoWidth + 2;
  }
  doc.setTextColor(...MUTED);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(report.tenantName, nameX, 16);
  // The line under the name moves the title, and the stamp beside it, down by
  // its height: otherwise the title's first line is printed over it. A long
  // legal name wraps rather than losing the CNPJ at the end.
  let offset = 0;
  if (report.tenantDetails) {
    const details = doc.splitTextToSize(
      report.tenantDetails,
      width - PAGE_MARGIN_MM - nameX
    ) as string[];
    doc.text(details, nameX, 16 + TENANT_DETAILS_LINE_MM);
    const detailsLineHeight =
      (doc.getFontSize() * doc.getLineHeightFactor()) / doc.internal.scaleFactor;
    offset = TENANT_DETAILS_LINE_MM + (details.length - 1) * detailsLineHeight;
  }

  const stampWidth = report.stamp ? drawStamp(doc, report.stamp, offset) : 0;
  doc.setTextColor(...FOREGROUND);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  // A title wider than the line wraps; everything below moves down by the
  // extra lines, or the subtitle is printed over the title's second line.
  const titleLines = doc.splitTextToSize(
    report.title,
    contentWidth - stampWidth
  ) as string[];
  doc.text(titleLines, PAGE_MARGIN_MM, 23 + offset);
  const titleLineHeight =
    (doc.getFontSize() * doc.getLineHeightFactor()) / doc.internal.scaleFactor;

  let y = 23 + offset + (titleLines.length - 1) * titleLineHeight;
  if (report.subtitle) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...SECONDARY_TEXT);
    const lines = doc.splitTextToSize(report.subtitle, contentWidth) as string[];
    doc.text(lines, PAGE_MARGIN_MM, y + 6);
    y += 6 + (lines.length - 1) * 4;
  }
  doc.setDrawColor(...FOREGROUND);
  doc.setLineWidth(0.6);
  doc.line(PAGE_MARGIN_MM, y + 4, width - PAGE_MARGIN_MM, y + 4);
  return y + 9;
}

/**
 * The callout box, `startY` being its top: the bold title, its pills on the
 * same line (a pill that does not fit starts the next line, its joiner word
 * with it) and the muted text below. The content is laid out first and the
 * border stroked last, around what was drawn. Returns where the next block
 * starts.
 */
function drawCallout(
  doc: jsPDF,
  callout: TablePdfCallout,
  startY: number
): number {
  const width = doc.internal.pageSize.getWidth();
  const left = PAGE_MARGIN_MM + CALLOUT_PADDING_MM;
  const right = width - PAGE_MARGIN_MM - CALLOUT_PADDING_MM;
  /** The vertical middle of the line being written. */
  let middle = startY + CALLOUT_PADDING_MM + PILL_HEIGHT_MM / 2;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...FOREGROUND);
  const titleLines = doc.splitTextToSize(callout.title, right - left) as string[];
  titleLines.forEach((line, index) => {
    if (index > 0) {
      middle += PILL_LINE_MM;
    }
    doc.text(line, left, middleBaseline(doc, middle));
  });
  let x =
    left + doc.getTextWidth(titleLines.at(-1) ?? '') + PILL_GAP_MM;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  (callout.pills ?? []).forEach((pill, index) => {
    const joiner = index > 0 ? callout.pillJoiner ?? '' : '';
    const joinerWidth = joiner ? doc.getTextWidth(joiner) + PILL_GAP_MM : 0;
    const pillWidth = doc.getTextWidth(pill) + PILL_PADDING_MM * 2;
    if (x + joinerWidth + pillWidth > right && x > left) {
      middle += PILL_LINE_MM;
      x = left;
    }
    const baseline = middleBaseline(doc, middle);
    if (joiner) {
      doc.setTextColor(...MUTED);
      doc.text(joiner, x, baseline);
      x += joinerWidth;
    }
    doc.setFillColor(...HEADER_FILL);
    doc.roundedRect(x, middle - PILL_HEIGHT_MM / 2, pillWidth, PILL_HEIGHT_MM, 1, 1, 'F');
    doc.setTextColor(...FOREGROUND);
    doc.text(pill, x + PILL_PADDING_MM, baseline);
    x += pillWidth + PILL_GAP_MM;
  });

  let bottom = middle + PILL_HEIGHT_MM / 2;
  if (callout.text) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...MUTED);
    const lines = doc.splitTextToSize(callout.text, right - left) as string[];
    const lineHeight =
      (doc.getFontSize() * doc.getLineHeightFactor()) / doc.internal.scaleFactor;
    const firstBaseline = bottom + 1.5 + lineHeight * 0.8;
    doc.text(lines, left, firstBaseline);
    // The last line's descenders.
    bottom = firstBaseline + (lines.length - 1) * lineHeight + lineHeight * 0.3;
  }

  const boxBottom = bottom + CALLOUT_PADDING_MM;
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.2);
  doc.roundedRect(
    PAGE_MARGIN_MM,
    startY,
    width - PAGE_MARGIN_MM * 2,
    boxBottom - startY,
    1.5,
    1.5,
    'S'
  );
  return boxBottom + CALLOUT_GAP_MM;
}

function drawSummary(
  doc: jsPDF,
  autoTable: AutoTable,
  blocks: TablePdfSummaryBlock[],
  startY: number
): number {
  let y = startY;
  for (const block of blocks.filter((item) => item.items.length)) {
    if (block.title) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...FOREGROUND);
      doc.text(block.title, PAGE_MARGIN_MM, y + 3);
      y += 6;
    }
    // A third row only when some item has a caption: a block without one
    // keeps its two rows.
    const captioned = block.items.some((item) => item.caption);
    autoTable(doc, {
      startY: y,
      theme: 'plain',
      body: [
        block.items.map((item) => item.value),
        block.items.map((item) => item.label),
        ...(captioned ? [block.items.map((item) => item.caption ?? '')] : []),
      ],
      styles: {
        fontSize: 8,
        textColor: MUTED,
        cellPadding: { top: 0.6, right: 2.4, bottom: 2.4, left: 2.4 },
      },
      tableLineColor: BORDER,
      tableLineWidth: 0.2,
      margin: { left: PAGE_MARGIN_MM, right: PAGE_MARGIN_MM, bottom: FOOTER_SPACE_MM },
      didParseCell: (data: CellHookData) => {
        if (data.row.index === 0) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 11;
          data.cell.styles.textColor = FOREGROUND;
          data.cell.styles.cellPadding = { top: 2.4, right: 2.4, bottom: 0.6, left: 2.4 };
        } else if (captioned && data.row.index === 1) {
          data.cell.styles.cellPadding = { top: 0.6, right: 2.4, bottom: 0.4, left: 2.4 };
        } else if (data.row.index === 2) {
          data.cell.styles.fontSize = 7;
          data.cell.styles.cellPadding = { top: 0, right: 2.4, bottom: 2.4, left: 2.4 };
        }
      },
    });
    y = lastTableY(doc, y) + 6;
  }
  return y;
}

function groupRow(
  row: TablePdfGroupRow,
  columnCount: number,
  inlineDetail = false
): RowInput {
  const styles: Partial<Styles> = {
    fillColor: GROUP_FILL,
    fontStyle: 'bold',
    textColor: FOREGROUND,
    // A spanning cell would otherwise inherit the first column's alignment.
    halign: 'left',
  };
  // An inline detail is drawn over the cell once it is drawn (`drawInlineDetail`).
  if (!row.detail || inlineDetail) {
    return [{ content: row.group, colSpan: columnCount, styles }];
  }
  if (columnCount < 2) {
    return [{ content: `${row.group} · ${row.detail}`, styles }];
  }
  return [
    { content: row.group, colSpan: columnCount - 1, styles },
    {
      content: row.detail,
      styles: { ...styles, fontStyle: 'normal', textColor: MUTED, halign: 'right' },
    },
  ];
}

/**
 * The baseline of a line of the current font centred on `middle`, where
 * autotable writes a cell's text (`autoTableText`), so text drawn by hand
 * lines up with the cell's own.
 */
function middleBaseline(doc: jsPDF, middle: number): number {
  const fontSize = doc.getFontSize() / doc.internal.scaleFactor;
  return middle + fontSize * (2 - 1.15) - (fontSize * doc.getLineHeightFactor()) / 2;
}

/** A group row's detail on the right edge of its one cell, as `groupRow` styles it. */
function drawInlineDetail(doc: jsPDF, detail: string, data: CellHookData): void {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text(
    detail,
    data.cell.x + data.cell.width - 2,
    middleBaseline(doc, data.cell.y + data.cell.height / 2),
    { align: 'right' }
  );
}

/** `total`, then the rows of `totalBreakdown`. */
function totalRows(table: TablePdfTable): RowInput[] {
  const columnCount = table.columns.length;
  const totals = [table.total, ...(table.totalBreakdown ?? [])].filter(
    (total): total is TablePdfTotal => !!total
  );
  return totals.map((total) => {
    const value: CellDef = {
      content: total.value,
      styles: { halign: 'right', ...(total.accent ? { textColor: ACCENT } : {}) },
    };
    return columnCount < 2
      ? [`${total.label} · ${total.value}`]
      : [
          { content: total.label, colSpan: columnCount - 1, styles: { halign: 'left' } },
          value,
        ];
  });
}

/** A table's title on the left and its detail on the right edge, `y` being the top. */
function drawHeadingText(
  doc: jsPDF,
  title: string | undefined,
  detail: string | undefined,
  y: number
): void {
  const baseline = y + 4;
  if (title) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...FOREGROUND);
    doc.text(title, PAGE_MARGIN_MM, baseline);
  }
  if (detail) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(detail, doc.internal.pageSize.getWidth() - PAGE_MARGIN_MM, baseline, {
      align: 'right',
    });
  }
}

/** The table's page break and heading; returns where the table starts. */
function drawTableHeading(
  doc: jsPDF,
  table: TablePdfTable,
  startY: number
): number {
  let y = startY;
  if (table.newPage) {
    doc.addPage();
    y = PAGE_MARGIN_MM;
  }
  if (!table.title && !table.detail) {
    return y;
  }
  const bottomLimit = doc.internal.pageSize.getHeight() - FOOTER_SPACE_MM;
  const keep =
    TABLE_HEADING_KEEP_MM + (table.totalBreakdown?.length ?? 0) * TOTAL_ROW_MM;
  if (y + keep > bottomLimit) {
    doc.addPage();
    y = PAGE_MARGIN_MM;
  }
  drawHeadingText(doc, table.title, table.detail, y);
  return y + TABLE_HEADING_MM;
}

/**
 * The hooks that mark a table's later pages (`continuation`): its heading at
 * the top, and the group row the page break cut, under the header row.
 *
 * The repeated group row is painted by hand, as the first row of the page is
 * about to be drawn: autotable has no way to add a row of its own there. That
 * row was measured without it, so it can reach as far into the bottom margin
 * as one line is tall; the first row of a page never comes near it.
 */
function continuationHooks(
  doc: jsPDF,
  table: TablePdfTable,
  continuation: string
): {
  willDrawPage: (data: HookData) => void;
  willDrawCell: (data: CellHookData) => void;
  didDrawCell: (data: CellHookData) => void;
} {
  let group: TablePdfGroupRow | null = null;
  let pageStarts = false;
  return {
    willDrawPage: (data) => {
      if (data.pageNumber < 2 || !data.cursor) {
        return;
      }
      pageStarts = true;
      if (table.title || table.detail) {
        drawHeadingText(
          doc,
          table.title ? `${table.title} · ${continuation}` : undefined,
          table.detail,
          data.cursor.y
        );
        data.cursor.y += TABLE_HEADING_MM;
      }
    },
    willDrawCell: (data) => {
      if (data.section !== 'body' || !pageStarts || !data.cursor) {
        return;
      }
      pageStarts = false;
      const row = table.rows[data.row.index];
      // A page that opens on a new group needs no repeat.
      if (!group || !row || isGroupRow(row)) {
        return;
      }
      const height = drawGroupBand(
        doc,
        { group: `${group.group} · ${continuation}`, detail: group.detail },
        data.cursor.y
      );
      data.cursor.y += height;
      data.cell.y = data.cursor.y;
    },
    didDrawCell: (data) => {
      const row = data.section === 'body' ? table.rows[data.row.index] : undefined;
      if (row && isGroupRow(row)) {
        group = row;
      }
    },
  };
}

/**
 * A group row drawn by hand, as `groupRow` styles it, on one line across the
 * content width; returns its height. It is drawn after autotable set the
 * styles of the cell that follows, so it puts them back.
 */
function drawGroupBand(doc: jsPDF, row: TablePdfGroupRow, y: number): number {
  const font = doc.getFont();
  const fontSize = doc.getFontSize();
  const textColor = doc.getTextColor();
  const fillColor = doc.getFillColor();
  const drawColor = doc.getDrawColor();
  const lineWidth = doc.getLineWidth();
  const left = PAGE_MARGIN_MM;
  const right = doc.internal.pageSize.getWidth() - PAGE_MARGIN_MM;
  // One 8pt line and the table's 2mm padding above and below.
  const height = (8 * doc.getLineHeightFactor()) / doc.internal.scaleFactor + 4;
  doc.setFillColor(...GROUP_FILL);
  doc.rect(left, y, right - left, height, 'F');
  doc.setFontSize(8);
  const baseline = middleBaseline(doc, y + height / 2);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...FOREGROUND);
  doc.text(row.group, left + 2, baseline);
  if (row.detail) {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...MUTED);
    doc.text(row.detail, right - 2, baseline, { align: 'right' });
  }
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.2);
  doc.line(left, y + height, right, y + height);

  doc.setFont(font.fontName, font.fontStyle);
  doc.setFontSize(fontSize);
  doc.setTextColor(textColor);
  doc.setFillColor(fillColor);
  doc.setDrawColor(drawColor);
  doc.setLineWidth(lineWidth);
  return height;
}

function drawTable(
  doc: jsPDF,
  autoTable: AutoTable,
  table: TablePdfTable,
  formatter: TableExportFormatter | undefined,
  startY: number
): number {
  const columnCount = table.columns.length;
  const aligns = table.columns.map(
    (column) => column.align ?? (column.type === 'number' ? 'right' : 'left')
  );
  const foot = totalRows(table);
  const continuation = table.continuation
    ? continuationHooks(doc, table, table.continuation)
    : null;
  autoTable(doc, {
    startY,
    theme: 'plain',
    head: [table.columns.map((column) => column.header)],
    body: table.rows.map((row) =>
      isGroupRow(row)
        ? groupRow(row, columnCount, table.inlineGroupDetail)
        : table.columns.map((column, index) =>
            tableExportText(row[index], column, formatter)
          )
    ),
    foot,
    showFoot: 'lastPage',
    // A row split across pages loses its place in `table.rows` (autotable
    // numbers the second half -1): no repeated group above it, no inline
    // detail. These tables keep each row whole on one page.
    ...(table.continuation || table.inlineGroupDetail
      ? { rowPageBreak: 'avoid' as const }
      : {}),
    styles: { fontSize: 8, textColor: FOREGROUND, cellPadding: 2, valign: 'middle' },
    headStyles: { fillColor: HEADER_FILL, textColor: MUTED, fontStyle: 'bold' },
    footStyles: { fillColor: HEADER_FILL, textColor: FOREGROUND, fontStyle: 'bold' },
    columnStyles: Object.fromEntries(
      table.columns.map((column, index) => [
        index,
        {
          halign: aligns[index],
          ...(column.pdfWidth ? { cellWidth: column.pdfWidth } : {}),
        },
      ])
    ),
    margin: {
      top: PAGE_MARGIN_MM,
      left: PAGE_MARGIN_MM,
      right: PAGE_MARGIN_MM,
      bottom: FOOTER_SPACE_MM,
    },
    didParseCell: (data: CellHookData) => {
      // `columnStyles` reach the body only; the header follows its column.
      if (data.section === 'head') {
        data.cell.styles.halign = aligns[data.column.index];
      }
    },
    ...(continuation
      ? {
          willDrawPage: continuation.willDrawPage,
          willDrawCell: continuation.willDrawCell,
        }
      : {}),
    didDrawCell: (data: CellHookData) => {
      // Body rows, and closing rows but the last, end on a line.
      const ruled =
        data.section === 'body' ||
        (data.section === 'foot' && data.row.index < foot.length - 1);
      if (ruled) {
        const bottom = data.cell.y + data.cell.height;
        doc.setDrawColor(...BORDER);
        doc.setLineWidth(0.2);
        doc.line(data.cell.x, bottom, data.cell.x + data.cell.width, bottom);
      }
      const row = data.section === 'body' ? table.rows[data.row.index] : undefined;
      if (table.inlineGroupDetail && row && isGroupRow(row) && row.detail) {
        drawInlineDetail(doc, row.detail, data);
      }
      continuation?.didDrawCell(data);
    },
  });
  return lastTableY(doc, startY);
}

/** Returns where the last note ends. */
function drawNotes(doc: jsPDF, notes: TablePdfNote[], startY: number): number {
  const contentWidth = doc.internal.pageSize.getWidth() - PAGE_MARGIN_MM * 2;
  const bottomLimit = doc.internal.pageSize.getHeight() - FOOTER_SPACE_MM;
  let y = startY + 7;
  for (const note of notes) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const lines = doc.splitTextToSize(note.text, contentWidth) as string[];
    const lineHeight =
      (doc.getFontSize() * doc.getLineHeightFactor()) / doc.internal.scaleFactor;
    const textHeight = lines.length * lineHeight;
    const height = (note.title ? 4.5 : 0) + textHeight;
    if (y + height > bottomLimit) {
      doc.addPage();
      y = PAGE_MARGIN_MM + 4;
    }
    if (note.title) {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...FOREGROUND);
      doc.text(note.title, PAGE_MARGIN_MM, y);
      y += 4.5;
    }
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...SECONDARY_TEXT);
    doc.text(lines, PAGE_MARGIN_MM, y);
    y += textHeight + 4;
  }
  return y;
}

function drawFooters(doc: jsPDF, report: TablePdfDocument): void {
  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();
  const total = doc.getNumberOfPages();
  for (let page = 1; page <= total; page++) {
    doc.setPage(page);
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.2);
    doc.line(PAGE_MARGIN_MM, height - 14, width - PAGE_MARGIN_MM, height - 14);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...MUTED);
    doc.text(report.generatedLabel, PAGE_MARGIN_MM, height - 9);
    doc.text(report.pageLabel(page, total), width - PAGE_MARGIN_MM, height - 9, {
      align: 'right',
    });
  }
}

/**
 * A4 PDF around one table, or several: the gym's logo and name, title, subtitle
 * and an optional stamp ("Simulação"); an optional callout box (the rule in
 * force); optional summary blocks; `table` and then
 * each of `tables` (group rows, closing total rows, a heading repeated on its
 * later pages, a page break and notes of its own allowed); optional notes; and
 * on every page the footer "Gerado em … por …" / "Página N de M".
 *
 * The footer is stamped once, after the last table, so "Página N de M" counts
 * the pages of every table.
 *
 * `jspdf` and `jspdf-autotable` are loaded on demand, so they stay out of every
 * bundle that imports `@shared/utils` without printing anything.
 */
export async function buildTablePdf(
  report: TablePdfDocument,
  formatter?: TableExportFormatter
): Promise<jsPDF> {
  const [{ jsPDF: JsPdf }, { autoTable }] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ]);
  const doc = new JsPdf({
    orientation: report.orientation ?? 'portrait',
    unit: 'mm',
    format: 'a4',
  });
  // Every text reaches the page through `doc.text`, autoTable's cells too:
  // this is the one place that sees them all. jsPDF's `preProcessText` event
  // fires after the string was already encoded, so it is too late for this.
  const text = doc.text.bind(doc);
  doc.text = (value, ...rest) =>
    text(
      Array.isArray(value) ? value.map(drawableText) : drawableText(value),
      ...rest
    );
  const headerEnd = drawHeader(doc, report);
  const summaryY = report.callout
    ? drawCallout(doc, report.callout, headerEnd)
    : headerEnd;
  let y = drawSummary(doc, autoTable, report.summary ?? [], summaryY);
  [report.table, ...(report.tables ?? [])].forEach((table, index) => {
    y = drawTableHeading(doc, table, index === 0 ? y : y + TABLE_GAP_MM);
    y = drawTable(doc, autoTable, table, formatter, y);
    if (table.notes?.length) {
      y = drawNotes(doc, table.notes, y);
    }
  });
  if (report.notes?.length) {
    drawNotes(doc, report.notes, y);
  }
  drawFooters(doc, report);
  return doc;
}

/** Downloads `buildTablePdf` as `<fileBaseName>.pdf`. */
export async function exportTablePdf(
  fileBaseName: string,
  report: TablePdfDocument,
  formatter?: TableExportFormatter
): Promise<void> {
  const doc = await buildTablePdf(report, formatter);
  doc.save(`${fileBaseName}.pdf`);
}
